export const chapterData = [
    {
        id: '1',
        title: 'Chapter 1: Introduction',
        icon: 'book',
        startPage: 22,
        objective: "To introduce autonomous network functions (SDN, SON, O-RAN) and the need for the Normative Reinforcement Control Framework (NRCF).",
        summary: "Overview of autonomous network functions (SDN, SON, O-RAN) and the need for the Normative Reinforcement Control Framework (NRCF).",
        sections: [
            { title: "Context", content: "The telecommunications industry is shifting from human-centric networks to connecting machines and autonomous systems (IoT, driverless vehicles), requiring ultra-reliable low latency." },
            { title: "The Problem", content: "Current management of 5G/B5G networks faces challenges with inconsistent optimisation, lack of transparency in AI operations, energy inefficiency, and algorithmic bias." },
            { title: "The Solution", content: "The research proposes the Normative Reinforcement Control Framework (NRCF). It is a hybrid framework integrating normative control with reinforcement learning to ensure autonomy, decision integrity, and explainability." },
            {
                title: "Key Objectives", items: [
                    "Establish methods for Explainable AI (XAI) to interpret network actions.",
                    "Develop anomaly detection techniques for stochastic algorithms (mitigating false positives/negatives).",
                    "Propose an \"Intelligent Plane\" for B5G network architecture to centralise AI governance."
                ]
            }
        ],
        keyFigures: [
            {
                src: '/images/renamed/Page_24_Figure_1-1_Cellular_Network_Authors_representation.png',
                alt: 'Figure 1.1: Cellular Network',
                caption: 'Figure 1.1: Cellular Network (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_29_Figure_1-2_SDN_Operation_Overview_adapted_from_Goransson_Black_Culver_2017.png',
                alt: 'Figure 1.2: SDN Operation Overview',
                caption: 'Figure 1.2: SDN Operation Overview (adapted from Goransson, Black & Culver, 2017)'
            },
            {
                src: '/images/renamed/Page_30_Figure_1-3_Virtualised_Network_using_Overlays.png',
                alt: 'Figure 1.3: Virtualised Network using Overlays',
                caption: 'Figure 1.3: Virtualised Network using Overlays (adapted from Jain & Paul, 2013)'
            },
            {
                src: '/images/renamed/Page_32_Figure_1-4_Physical_Cell_ID_Allocation.png',
                alt: 'Figure 1.4: Physical Cell ID Allocation',
                caption: 'Figure 1.4: Physical Cell ID Allocation (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_33_Figure_1-6_Automatic_Neighbour_Relations_ANR_Management_Authors_representation_2.png',
                alt: 'Figure 1.5: Physical Random-Access Channel Configuration',
                caption: 'Figure 1.5: Physical Random-Access Channel Configuration (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_33_Figure_1-5_Physical_Random-Access_Channel_Configuration_Authors_representation_2.png',
                alt: 'Figure 1.6: Automatic Neighbour Relations (ANR) Management',
                caption: 'Figure 1.6: Automatic Neighbour Relations (ANR) Management (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_33_Figure_1-7_Coverage_and_Capacity_Optimisation.png',
                alt: 'Figure 1.7: Coverage and Capacity Optimisation',
                caption: 'Figure 1.7: Coverage and Capacity Optimisation (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_34_Figure_1-8_Optimised_Network_with_Cell_Outage_Compensation_Authors_representatio.png',
                alt: 'Figure 1.8: Optimised Network with Cell Outage Compensation',
                caption: 'Figure 1.8: Optimised Network with Cell Outage Compensation (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_34_Figure_1-9_Mobility_Load_Balancing_Concept_Authors_representation_2024.png',
                alt: 'Figure 1.9: Mobility Load Balancing Concept',
                caption: 'Figure 1.9: Mobility Load Balancing Concept (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_34_Figure_1-10_Offloading_Users_from_Congested_Cells_to_Adjacent_Cells_Authors_repr.png',
                alt: 'Figure 1.10: Offloading Users from Congested Cells',
                caption: 'Figure 1.10: Offloading Users from Congested Cells to Adjacent Cells (Author’s representation, 2024)'
            },
            {
                src: '/images/renamed/Page_36_Figure_1-11_O-RAN_Architecture_Overview.png',
                alt: 'Figure 1.11: O-RAN Architecture Overview',
                caption: 'Figure 1.11: O-RAN Architecture Overview (adapted from O-RAN Alliance, 2024)'
            },
            {
                src: '/images/renamed/Page_41_Figure_1-12_Autonomic_Closed_Control_Loop_for_Network_Management_Fallon_Keeney_a.png',
                alt: 'Figure 1.12: Autonomic Closed Control Loop',
                caption: 'Figure 1.12: Autonomic Closed Control Loop for Network Management (Fallon, Keeney & O’Sullivan, 2011)'
            }
        ]
    },
    {
        id: '2',
        title: 'Chapter 2: Literature Review',
        icon: '📚',
        startPage: 52,
        objective: 'To critically review existing literature on autonomous network functions, normative control, and explainable AI.',
        summary: 'Critically reviews work on autonomous control, normative/constraint-based methods, explainable AI, and bias/drift handling in telecoms. Synthesises standards and architectures (e.g., O-RAN, ITU/ETSI) with research on SDN/SON orchestration and AI governance.',
        sections: [
            { title: "Technology Landscape", content: "Reviews the evolution of Autonomous Network Functions (ANFs) including SDN (Software Defined Networks), SON (Self-Organising Networks), and O-RAN (Open Radio Access Network)." },
            { title: "The Shift", content: "The industry is moving from deterministic (rule-based) algorithms to cognitive/stochastic (AI/ML) algorithms. While adaptable, stochastic models introduce unpredictability and bias." },
            { title: "Normative Control", content: "Introduces the concept of \"Normative Control\" as a mechanism to regulate autonomous entities through norms, sanctions, and incentives, ensuring they adhere to operator goals." },
            { title: "The Gap", content: "Current frameworks lack explicit normative safeguards to mitigate bias, lack sufficient explainability for \"black-box\" decisions, and struggle to align autonomous functions with real-time regulatory/operator objectives." }
        ],
        keyFigures: [
            {
                src: '/images/renamed/Page_54_Figure_2-1_Relation_between_SDN_DevOps_and_AI_Kim_H_and_Feamster_N_2013_Kreutz_e.png',
                alt: 'Figure 2.1: Relation between SDN, DevOps, and AI',
                caption: 'Figure 2.1: Relation between SDN, DevOps, and AI (Kim, H. and Feamster, N. 2013 ; Kreutz et.al 2015)'
            },
            {
                src: '/images/renamed/Page_57_Figure_2-2_SON_feature_and_functions_3GPP_2011.png',
                alt: 'Figure 2.2: SON feature and functions',
                caption: 'Figure 2.2: SON feature and functions (3GPP, 2011)'
            },
            {
                src: '/images/renamed/Page_61_Figure_2-3_System_Overview_of_the_Norm_Manager_Wolfgang_et_al_2016_p_73.png',
                alt: 'Figure 2.3: System Overview of the Norm Manager',
                caption: 'Figure 2.3: System Overview of the Norm Manager (Wolfgang et al., 2016)'
            },
            {
                src: '/images/renamed/Page_62_Figure_2-4_ALife_wains_process_de_Buitléir_et_al_2018.png',
                alt: 'Figure 2.4: ALife "wains" process',
                caption: 'Figure 2.4: ALife "wains" process (de Buitléir et al., 2018)'
            },
            {
                src: '/images/renamed/Page_70_Figure_2-5_Discrimination_Aware_Classifier_Build_Process_adapted_from_dAlessandr.png',
                alt: 'Figure 2.5: Discrimination Aware Classifier Build Process',
                caption: 'Figure 2.5: Discrimination Aware Classifier Build Process (adapted from d’Alessandro, O’Sullivan & O’Sullivan, 2017)'
            }
        ]
    },
    {
        id: '3',
        title: 'Chapter 3: Conceptual Framework',
        icon: '🧠',
        startPage: 107,
        objective: 'To develop the conceptual design and research methodology for the Normative-Reinforcement Control Framework (NRCF).',
        summary: 'Develops the conceptual design and research methodology for NRCF as the thesis’s core theoretical contribution. Formalises normative logic, reinforcement learning integration, and explainability hooks.',
        sections: [
            { title: "The 7-Layer Stack", content: "Defines the AI/ML software stack in telecoms (from data collection to application layers) and identifies specific biases inherent at each layer (e.g., sample bias, cognitive bias)." },
            {
                title: "Novel Measurement Scales", items: [
                    "GANFS (Glasgow Autonomous Network Function Scale): Assessing consciousness/responsiveness.",
                    "MEGABITS Score: Inspired by the Apgar score for rapid health assessment.",
                    "Gleason-Style Grading: For assessing the severity of algorithmic \"malignancy\" (errors/bias)."
                ]
            },
            {
                title: "NRCF Components", items: [
                    "NRCF-DA: Data Analyser.",
                    "NRCF-AR: Algorithm Result Evaluator.",
                    "NRCF-EV: Evaluator.",
                    "NRCF-NCF: Normative Control Function."
                ]
            }
        ],
        keyFigures: [
            {
                src: '/images/renamed/Page_109_Figure_31_Autonomous_network_functions_leverage_software_frameworks_platforms_an.png',
                alt: 'Figure 3.1: Autonomous network functions leverage software frameworks',
                caption: 'Figure 3.1: Autonomous network functions leverage software frameworks, platforms and libraries'
            },
            {
                src: '/images/renamed/Page_112_Figure_3-2_Indication_of_Area_above_curve_Authors_representation_Narayanan_and_H.png',
                alt: 'Figure 3.2: Indication of Area above curve',
                caption: 'Figure 3.2: Indication of Area above curve (Author’s representation; Narayanan and Harrison, 2022)'
            },
            {
                src: '/images/renamed/Page_129_Figure_34_The_final_Gleason_score_calculated_by_summing_the_primary_and_secondar.png',
                alt: 'Figure 3.4: The final Gleason score',
                caption: 'Figure 3.4: The final Gleason score calculated by summing the primary and secondary grades'
            },
            {
                src: '/images/renamed/Page_138_Figure_3-5_Algorithm_fairness_functions_supported_by_AI_fairness_360_Bellamy_et_.png',
                alt: 'Figure 3.5: Algorithm fairness functions supported by AI fairness 360',
                caption: 'Figure 3.5: Algorithm fairness functions supported by AI fairness 360 (Bellamy et al., 2018)'
            },
            {
                src: '/images/renamed/Page_146_Figure_3-7_CRISP-MLQ_Framework_illustrating_phases_with_associated_quality_contr.png',
                alt: 'Figure 3.7: CRISP-ML(Q) Framework',
                caption: 'Figure 3.7: CRISP-ML(Q) Framework illustrating phases with associated quality control tasks'
            },
            {
                src: '/images/renamed/Page_148_Figure_3-8_Publication_trends_showing_the_growing_interest_in_interpretable_ML_m.png',
                alt: 'Figure 3.8: Publication trends showing the growing interest in interpretable ML',
                caption: 'Figure 3.8: Publication trends showing the growing interest in interpretable ML models'
            }
        ]
    },
    {
        id: '4',
        title: 'Chapter 4: Anatomy of NRCF',
        icon: '⚙️',
        startPage: 155,
        objective: 'To detail the internal components, algorithms, and methods of the NRCF.',
        summary: 'Details NRCF internals: Data Analyser (NRCF-DA), Algorithm Result Evaluator (NRCF-AR), Evaluator (NRCF-EV), and Normative Control Function (NRCF-NCF). Describes algorithms for anomaly/drift detection, bias measurement, and interpretability.',
        sections: [
            { title: "NRCF-DA (Data Analyser)", content: "Validates input data integrity using statistical laws. Note: Newcomb-Benford’s Law was tested but found unsuitable for engineered telecom KPIs; Entropy and Percentile checks were effective." },
            {
                title: "NRCF-AR (Algorithm Result Evaluator)", items: [
                    "ANOBIA Model: (ANomaly and Outlier detection for Bias Identification). Uses statistical boundaries and outlier detection to flag malicious proposals.",
                    "INFEROBIA Model: Uses decision trees and distance metrics to explain why a proposal is malicious (Inference/Explainability)."
                ]
            },
            { title: "NRCF-NCF (Normative Control)", content: "Uses the Counterfactual Explanation Algorithm (CEA) to sequence and prioritise ANFs (e.g., ensuring mobility optimisation happens before energy saving)." }
        ],
        keyFigures: [
            {
                src: '/images/renamed/Page_155_Figure_4-1_Data_mining_process_CRISP-DM_and_machine_learning_application_CRISP-M.png',
                alt: 'Figure 4.1: Data mining process (CRISP-DM)',
                caption: 'Figure 4.1: Data mining process (CRISP-DM) and machine learning application (CRISP-ML (Q))'
            },
            {
                src: '/images/renamed/Page_157_Figure_4-2_Anatomy_of_autonomous_network_function_Adapted_from_Narayanan_and_Har.png',
                alt: 'Figure 4.2: Anatomy of autonomous network function',
                caption: 'Figure 4.2: Anatomy of autonomous network function (Adapted from Narayanan and Harrison, 2022)'
            },
            {
                src: '/images/renamed/Page_161_Figure_4-3_ANF_-_Analysing_consistency_of_Input_Data_Authors_representation.png',
                alt: 'Figure 4.3: ANF - Analysing consistency of Input Data',
                caption: 'Figure 4.3: ANF - Analysing consistency of Input Data (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_163_Figure_4-4_Benfords_law_frequency_of_occurrence_of_leading_digits_for_100_sample.png',
                alt: 'Figure 4.4: Benford’s law',
                caption: 'Figure 4.4: Benford’s law (frequency of occurrence of leading digits)'
            },
            {
                src: '/images/renamed/Page_174_Figure_4-5_Box_and_whisker_Plot_Illustration_adapted_from_Spear_1969.png',
                alt: 'Figure 4.5: Box and whisker Plot Illustration',
                caption: 'Figure 4.5: Box and whisker Plot Illustration (adapted from Spear 1969)'
            },
            {
                src: '/images/renamed/Page_180_Figure_4-6_ANF_-_Analysing_Algorithm_Parameters_Authors_representation.png',
                alt: 'Figure 4.6: ANF - Analysing Algorithm Parameters',
                caption: 'Figure 4.6: ANF - Analysing Algorithm Parameters (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_184_Figure_4-7_ANF_-_Analysing_algorithm_recommendations_Authors_representation.png',
                alt: 'Figure 4.7: ANF - Analysing algorithm recommendations',
                caption: 'Figure 4.7: ANF - Analysing algorithm recommendations (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_191_Figure_49_demonstrates_the_detection_mechanism_graphically.png',
                alt: 'Figure 4.9: Detection mechanism graphically',
                caption: 'Figure 4.9 demonstrates the detection mechanism graphically'
            },
            {
                src: '/images/renamed/Page_196_Figure_4-9_Illustration_of_clusters_C1_C2_and_outliers_O1_O2_Authors_representat.png',
                alt: 'Figure 4.9: Illustration of clusters and outliers',
                caption: 'Figure 4.9: Illustration of clusters (C1, C2) and outliers (O1, O2) (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_206_Figure_4-10_Decision_tree_to_infer_reasons_for_malicious_algorithm_proposals_Aut.png',
                alt: 'Figure 4.10: Decision tree to infer reasons for malicious algorithm proposals',
                caption: 'Figure 4.10: Decision tree to infer reasons for malicious algorithm proposals (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_209_Figure_4-11_Decision_tree_to_infer_reasons_for_malicious_algorithm_proposal_Auth.png',
                alt: 'Figure 4.11: Decision tree to infer reasons for malicious algorithm proposal',
                caption: 'Figure 4.11: Decision tree to infer reasons for malicious algorithm proposal (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_216_Figure_4-12_Counterfactual_Explanation_algorithm_for_ANF_Authors_representation.png',
                alt: 'Figure 4.12: Counterfactual Explanation algorithm for ANF',
                caption: 'Figure 4.12: Counterfactual Explanation algorithm for ANF (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_222_Figure_4-13_Anatomy_of_Normative_Reinforcement_Control_Framework_Authors_represe.png',
                alt: 'Figure 4.13: Anatomy of Normative Reinforcement Control Framework',
                caption: 'Figure 4.13: Anatomy of Normative Reinforcement Control Framework (Author’s representation)'
            }
        ]
    },
    {
        id: '5',
        title: 'Chapter 5: Experimental Setup',
        icon: '🧪',
        startPage: 230,
        objective: 'To describe the experimental environment and use cases for validating the NRCF.',
        summary: 'Describes the experimental environment, datasets, and implementation used to instantiate NRCF. Defines use cases centred on sustainable energy-saving functions in LTE/5G.',
        sections: [
            { title: "The Use Case", content: "Focuses on Energy Saving Activation Functions (EAF) in LTE networks." },
            { title: "Objective", content: "To \"Break the Energy Curve\" by optimising energy consumption without degrading Quality of Service (QoS)." },
            {
                title: "Scenario", items: [
                    "Addressed the reality that Energy Consumption Meters are not installed everywhere. The framework needed to:",
                    "Predict energy consumption using proxy KPIs.",
                    "Analyze discrepancies in energy usage between similar network cells."
                ]
            },
            { title: "Setup", content: "The NRCF components were deployed to monitor, validate, and control the EAF recommendations in a simulated Tier-1 operator environment." }
        ],
        keyFigures: [
            {
                src: '/images/renamed/Page_230_Figure_5-1_NRCF_-_Experiment_Setup_Authors_representation.png',
                alt: 'Figure 5.1: NRCF - Experiment Setup',
                caption: 'Figure 5.1: NRCF - Experiment Setup (Author’s representation)'
            },
            {
                src: '/images/renamed/Page_231_Figure_5-2_Breaking_the_Energy_Curve_Adapted_from_Ericsson_2020Ericsson2022.png',
                alt: 'Figure 5.2: Breaking the Energy Curve',
                caption: 'Figure 5.2: Breaking the Energy Curve (Adapted from Ericsson (2020);Ericsson(2022))'
            },
            {
                src: '/images/renamed/Page_233_Figure_5-3_Improving_Energy_Efficiency_of_Radio_Networks_Authors_Representation.png',
                alt: 'Figure 5.3: Improving Energy Efficiency of Radio Networks',
                caption: 'Figure 5.3: Improving Energy Efficiency of Radio Networks (Author’s Representation)'
            }
        ]
    },
    {
        id: '6',
        title: 'Chapter 6: Empirical Evaluation',
        icon: '📊',
        startPage: 243,
        objective: 'To report the empirical findings and validation results of the NRCF components.',
        summary: 'Reports empirical analyses of network data through each NRCF component. Presents step-by-step experiments validating detection, evaluation, and control loops.',
        sections: [
            { title: "Data", content: "Analyzed 40 KPIs across 65,564 samples. Statistical tests (Welch’s t-test, KL Divergence) confirmed data stability between training and inference sets." },
            { title: "Prediction Results", content: "An XGBoost model predicted energy consumption categories (High/Low) with 90% accuracy." },
            { title: "Feature Importance", content: "Identified \"Maximum RRC Connections,\" \"Call Trace Data Availability,\" and \"Inter-Frequency Neighbour Relations\" as the top drivers of energy consumption." },
            {
                title: "Anomaly Detection", items: [
                    "ANOBIA: Successfully flagged 828 anomalies (approx 6.3%) in energy usage patterns.",
                    "INFEROBIA: The decision tree revealed that high-energy cells were characterised by high call volumes (>114k) and latency (>7.31ms)."
                ]
            }
        ],
        keyFigures: [
            {
                src: '/images/renamed/Page_273_Figure_6-1_Newcomb-Benfords_Law_on_Empirical_data_Authors_Representation.png',
                alt: 'Figure 6.1: Newcomb-Benford’s Law on Empirical data',
                caption: 'Figure 6.1: Newcomb-Benford’s Law on Empirical data (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_276_Figure_6-2_Box_and_Whisker_Plots_for_Network_KPIs_Authors_Representation.png',
                alt: 'Figure 6.2: Box and Whisker Plots for Network KPIs',
                caption: 'Figure 6.2: Box and Whisker Plots for Network KPIs (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_281_Figure_6-3_Kullback_Leibler_KPI_wise_Chart_1_Authors_Representation.png',
                alt: 'Figure 6.3: Kullback Leibler KPI wise Chart 1',
                caption: 'Figure 6.3: Kullback Leibler KPI wise Chart 1 (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_282_Figure_6-4_Kullback_Leibler_KPI_wise_Chart_2_Authors_Representation.png',
                alt: 'Figure 6.4: Kullback Leibler KPI wise Chart 2',
                caption: 'Figure 6.4: Kullback Leibler KPI wise Chart 2 (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_283_Figure_6-5_Kullback-Leibler_KL_Divergence_Value_Chart_for_all_KPIs_Authors_Repre.png',
                alt: 'Figure 6.5: Kullback-Leibler (KL) Divergence Value Chart',
                caption: 'Figure 6.5: Kullback-Leibler (KL) Divergence Value Chart for all KPIs (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_289_Figure_6-6_Outlier_deduction_-_MAD_Robust_Z-Score_Authors_Representation.png',
                alt: 'Figure 6.6: Outlier deduction - MAD, Robust Z-Score',
                caption: 'Figure 6.6: Outlier deduction - MAD, Robust Z-Score (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_299_Figure_6-7_Confusion_Matrix_for_Energy_Consumption_Meter_Prediction_Authors_Repr.png',
                alt: 'Figure 6.7: Confusion Matrix for Energy Consumption Meter Prediction',
                caption: 'Figure 6.7: Confusion Matrix for Energy Consumption Meter Prediction (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_304_Figure_6-8_Feature_importance_of_XGBoost_model_predicting_energy_Authors_Represe.png',
                alt: 'Figure 6.8: Feature importance of XGBoost model',
                caption: 'Figure 6.8: Feature importance of XGBoost model predicting energy (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_311_Figure_6-9_INFEROBIA_model_for_deriving_reasons_for_energy_consumption_Authors_R.png',
                alt: 'Figure 6.9: INFEROBIA model for deriving reasons for energy consumption',
                caption: 'Figure 6.9: INFEROBIA model for deriving reasons for energy consumption (Author’s Representation)'
            },
            {
                src: '/images/renamed/Page_314_Figure_6-10_Identifying_Anomalous_energy_usage_patterns_Authors_Representation.png',
                alt: 'Figure 6.10: Identifying Anomalous energy usage patterns',
                caption: 'Figure 6.10: Identifying Anomalous energy usage patterns (Author’s Representation)'
            }
        ]
    },
    {
        id: '7',
        title: 'Chapter 7: NRCF Test Findings',
        icon: '📝',
        startPage: 321,
        objective: 'To present NRCF test findings with empirical data on context and multi-stage optimisation techniques.',
        summary: 'Aggregates findings across experiments, compares optimisation strategies, and analyses component interdependencies. Derives design lessons and operational playbooks.',
        sections: [
            { title: "Conflict Reduction", content: "Integrating Normative Control (NCF) into the ASANA sequencing framework reduced conflicting ANF activations by 92.3%." },
            { title: "Effectiveness", content: "The NRCF effectively optimised energy consumption without adverse impacts on network performance." },
            {
                title: "Component Validation", items: [
                    "NRCF-EV: Scored the ANF maturity as 10/10 on the GANS and MEGABITS scales, indicating readiness for deployment."
                ]
            },
            { title: "Interpretation", content: "The use of SHAP values provided necessary transparency for operators to trust the automated decisions." },
            { title: "Recommendation", content: "Multi-stage optimisation (Preprocessing -> Prediction -> Validation) is required to handle dynamic network conditions." }
        ],
        keyFigures: []
    },
    {
        id: '8',
        title: 'Chapter 8: Intelligent Plane',
        icon: '🌐',
        startPage: 336,
        objective: 'To propose the NRCF as a foundation for a new Intelligent Plane in beyond-5G networks.',
        summary: 'Positions NRCF as a foundation for an additional “Intelligent Plane” in future architectures. Articulates limitations of embedding intelligence solely within control/data planes.',
        sections: [
            { title: "The Proposal", content: "Introduces a new architectural layer for B5G networks called the \"Intelligent Plane\"." },
            { title: "The Need", content: "Current architectures (Control Plane + Data Plane) cannot handle the complexity of distributed AI governance. Intelligence is currently too fragmented." },
            { title: "Functionality", content: "The Intelligent Plane centralises AI decision-making, anomaly detection (ANOBIA/INFEROBIA), and normative control, separating it from standard routing and configuration tasks." },
            { title: "Benefits", content: "Improves network efficiency, enhances security through real-time anomaly detection, and ensures better coordination of multiple ANFs." }
        ],
        keyFigures: []
    },
    {
        id: '9',
        title: 'Chapter 9: Conclusions and Future Work',
        icon: '💬',
        startPage: 352,
        objective: 'To conclude the thesis and discuss broader implications.',
        summary: 'Summarises contributions and answers the research questions. Reflects on scientific and practical significance, limitations, and ethical considerations.',
        sections: [
            { title: "Core Contribution", content: "The thesis delivers a validated framework (NRCF) that ensures autonomous networks are efficient, explainable, and ethically governed." },
            { title: "Key Outcome", content: "The Intelligent Plane is established as a foundational requirement for future self-governing networks." },
            {
                title: "Future Work", items: [
                    "Explore Federated Learning to preserve privacy.",
                    "Investigate Quantum-inspired computing to handle the computational load of the Intelligent Plane.",
                    "Implement A/B Testing for rigorous live-network validation."
                ]
            }
        ],
        keyFigures: []
    }
];
